import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, MessageCircle, XCircle, Compass } from "lucide-react";
import { api, formatApiError, fmtUSD } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { useCart } from "@/context/CartContext";
import { PrimaryButton, GhostButton } from "@/components/fields";

export default function PaymentResult({ kind }) {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(kind === "cancel" ? "cancelled" : "checking");
  const [villa, setVilla] = useState(null);
  const [items, setItems] = useState([]);
  const { clearCart } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    api.get("/villa").then((r) => setVilla(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (kind === "cancel" && sessionId) {
      api.post("/payments/release-hold", { session_id: sessionId }).catch(() => {});
    }
  }, [kind, sessionId]);

  useEffect(() => {
    if (kind !== "success") return;
    if (!sessionId) {
      setState("failed");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setItems(data.items || []);
          if (data.kind === "cart" && !cleared.current) {
            cleared.current = true;
            clearCart();
          }
          setState("paid");
          return;
        }
      } catch {
        /* keep polling */
      }
      if (++tries < 12) timer = setTimeout(poll, 1500);
      else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [kind, sessionId]);

  const waLink = villa?.whatsapp
    ? `https://wa.me/${villa.whatsapp}?text=${encodeURIComponent(t("payment.waText"))}`
    : null;

  return (
    <div className="max-w-xl mx-auto px-4 py-24 sm:py-32 text-center" data-testid="payment-result-page">
      {state === "checking" && (
        <>
          <Clock size={44} className="mx-auto text-gold animate-pulse" />
          <h1 className="font-serif text-3xl sm:text-4xl text-ink mt-6">{t("payment.checking")}</h1>
          <p className="text-ink/55 text-sm mt-3">{t("payment.checkingSub")}</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 size={48} className="mx-auto text-teal" data-testid="payment-paid-icon" />
          <h1 className="font-serif text-3xl sm:text-5xl text-ink mt-6" data-testid="payment-paid-title">
            {t("payment.paidA")} <span className="italic text-terracotta">{t("payment.paidB")}</span>
          </h1>
          <p className="text-ink/60 mt-4 leading-relaxed">{t("payment.paidSub")}</p>
          {items.length > 0 && (
            <div className="mt-6 rounded-2xl border border-ink/10 bg-white p-5 text-left space-y-2" data-testid="payment-order-items">
              <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/40">{t("cart.orderTitle")}</p>
              {items.map((i, idx) => (
                <div key={idx} className="flex justify-between gap-4 text-sm" data-testid="payment-order-item">
                  <span className="text-ink/80">{i.label} <span className="text-ink/45 text-xs block">{i.detail}</span></span>
                  <span className="font-semibold text-ink shrink-0">${fmtUSD(i.amount)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-10">
            <Link to="/account" data-testid="payment-view-trips-btn">
              <PrimaryButton className="w-full sm:w-auto">
                <Compass size={16} /> {t("payment.viewTrips")}
              </PrimaryButton>
            </Link>
            {waLink && (
              <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="payment-whatsapp-btn">
                <GhostButton className="w-full sm:w-auto !border-teal !text-teal hover:!bg-teal hover:!text-sand">
                  <MessageCircle size={16} /> {t("payment.whatsapp")}
                </GhostButton>
              </a>
            )}
          </div>
        </>
      )}
      {state === "pending" && (
        <>
          <Clock size={44} className="mx-auto text-gold" />
          <h1 className="font-serif text-3xl sm:text-4xl text-ink mt-6">{t("payment.pendingTitle")}</h1>
          <p className="text-ink/60 mt-4 leading-relaxed">{t("payment.pendingSub")}</p>
          <Link to="/account" data-testid="payment-pending-trips-btn" className="inline-block mt-8">
            <PrimaryButton>{t("payment.goTrips")}</PrimaryButton>
          </Link>
        </>
      )}
      {state === "cancelled" && (
        <>
          <XCircle size={44} className="mx-auto text-ink/30" data-testid="payment-cancelled-icon" />
          <h1 className="font-serif text-3xl sm:text-4xl text-ink mt-6">{t("payment.cancelledTitle")}</h1>
          <p className="text-ink/60 mt-4 leading-relaxed">{t("payment.cancelledSub")}</p>
          <Link to="/account" data-testid="payment-cancel-trips-btn" className="inline-block mt-8">
            <PrimaryButton>{t("payment.backTrips")}</PrimaryButton>
          </Link>
        </>
      )}
      {state === "failed" && (
        <>
          <XCircle size={44} className="mx-auto text-destructive" />
          <h1 className="font-serif text-3xl sm:text-4xl text-ink mt-6">{t("payment.failedTitle")}</h1>
          <p className="text-ink/60 mt-4 leading-relaxed">{t("payment.failedSub")}</p>
          <Link to="/account" data-testid="payment-failed-trips-btn" className="inline-block mt-8">
            <PrimaryButton>{t("payment.goTrips")}</PrimaryButton>
          </Link>
        </>
      )}
    </div>
  );
}
